import Decimal from 'decimal.js';
import { bool } from 'prop-types';
import React from 'react';
import cssLink from '../../styles/link.module.css';
import { formatMoney } from '../../util/currency';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { isLineItemCustomerDiscount, propTypes } from '../../util/types';
import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const hasCustomerDiscount = lineItems => {
  return lineItems.find(item => isLineItemCustomerDiscount(item.code));
};

const ContactSupportLink = ({ onClick }) => {
  return (
    <button className={cssLink['link-secondary']} onClick={onClick}>
      contact us
    </button>
  );
};

const LineItemProviderGlobalDiscountNote = props => {
  const { lineItems, transaction, isProvider, currency, intl } = props;

  if (!isProvider || !lineItems || !lineItems.length) {
    return null;
  }

  const customerDiscountLineItem = hasCustomerDiscount(lineItems);
  if (!customerDiscountLineItem) {
    return null;
  }

  // Check if this is a global discount
  const protectedDiscount = transaction?.attributes?.protectedData?.discount;
  const isGlobalDiscount = protectedDiscount?.discountType === 'GLOBAL';

  if (!isGlobalDiscount) {
    return null;
  }

  // Calculate the earnings: payout total + discount amount (discount is negative, so we add its absolute value)
  const currentPayoutTotal = transaction?.attributes?.payoutTotal;
  const discountAmount = customerDiscountLineItem.lineTotal;

  if (!currentPayoutTotal || !discountAmount) {
    return null;
  }

  // Create a Money object for total earnings by adding the absolute discount amount to the payout
  // Using Decimal for proper calculation
  const totalEarningsAmount = new Decimal(currentPayoutTotal.amount).plus(
    new Decimal(discountAmount.amount).abs()
  );
  const totalEarnings = new Money(totalEarningsAmount, currentPayoutTotal.currency);

  // Create Money object for the absolute discount amount
  const absoluteDiscountAmount = new Money(
    new Decimal(discountAmount.amount).abs(),
    discountAmount.currency
  );

  // Format all monetary values
  const formattedCurrentTotal = formatMoney(intl, currentPayoutTotal);
  const formattedDiscountAmount = formatMoney(intl, absoluteDiscountAmount);
  const formattedTotalEarnings = formatMoney(intl, totalEarnings);

  const handleContactSupport = event => {
    event.preventDefault();

    // eslint-disable-next-line no-undef
    if (typeof Tawk_API === 'undefined') {
      alert('Please contact support via email at info@soulwise.io');
      return;
    }
    Tawk_API.toggle();
  };

  return (
    <div className={css.lineItemNote}>
      <FormattedMessage
        id="OrderBreakdown.providerGlobalDiscountNoteDetailed"
        values={{
          totalEarnings: formattedTotalEarnings,
          currentTotal: formattedCurrentTotal,
          discountAmount: formattedDiscountAmount,
          lineBreak: <br />,
          contactUsLink: <ContactSupportLink onClick={handleContactSupport} />,
        }}
      />
    </div>
  );
};

LineItemProviderGlobalDiscountNote.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  transaction: propTypes.transaction.isRequired,
  isProvider: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemProviderGlobalDiscountNote;
